import {
  useMemo,
  useEffect,
  useRef,
  useCallback,
  type FC,
  useContext,
  type RefObject,
} from 'react';
import cn from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ModerationAlert from '@wix/da-ds/pkg/Icons/16x16/ModerationAlert';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import OnScrollBottom, { DISTANCE_UNIT } from '@wix/da-shared-react/pkg/OnScrollBottom';
import { useStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import { STUDIO_VIDEO_GENERATIONS_STREAM } from '../../../../constants';
import VideoCreatorPlaceholder from './VideoCreatorPlaceholder';
import VideoFile from './VideoFile';
import {
  getVideoGenerationsByIds,
  getVideoCreatorPendingGenerations,
} from '../../../selectors/studio';
import VideoPendingGeneration from './VideoPendingGeneration';
import { getModelPropertiesById } from '../ModelProperties';
import { VideoCreatorPendingGeneration } from '../../../types/store';
import {
  addVideoCreatorPendingGeneration,
  removeVideoCreatorPendingGeneration,
} from '../../../actions/studio/videoCreator';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';

import s from './VideoCreatorFiles.scss';

const SCROLL_FETCH_DISTANCE_PX = 1000;
const LOAD_MORE_COOLDOWN_MS = 500;

export interface Props {
  /** The element that scrolls (passed as a ref because mobile and desktop are different). */
  scrollContainerRef: RefObject<HTMLElement | null>;
}

const VideoCreatorFiles: FC<Props> = ({ scrollContainerRef }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const dispatch = useDispatch();
  const pendingGenerations = useSelector(getVideoCreatorPendingGenerations);

  const isFetchingRef = useRef(false);
  const loadMoreInFlightRef = useRef(false);
  const loadMoreCooldownUntilRef = useRef(0);

  const {
    items: generationIds = [],
    hasMore,
    fetchNext,
    isFetching,
  } = useStream<number>(STUDIO_VIDEO_GENERATIONS_STREAM.streamId);

  isFetchingRef.current = isFetching;

  useEffect(() => {
    if (!isFetching) {
      loadMoreInFlightRef.current = false;
    }
  }, [isFetching]);

  const videoGenerations = useSelector(state =>
    getVideoGenerationsByIds(state, generationIds || [])
  );

  useEffect(() => {
    const existingIds = new Set(pendingGenerations.map(p => p.generationId));
    const requestedVideos = videoGenerations.filter(
      video => video.status === 'requested' && !existingIds.has(video.generationId)
    );

    requestedVideos.forEach(video => {
      const modelProps = getModelPropertiesById(video.modelId);
      if (!modelProps) {
        return;
      }
      const pendingGeneration: VideoCreatorPendingGeneration = {
        generationId: video.generationId,
        modelType: modelProps.type,
        prompt: video.prompt,
        aspectRatio: video.aspectRatio,
        duration: video.duration,
        referenceDeviation: video.referenceDeviation,
        sound: video.sound ?? false,
      };
      dispatch(addVideoCreatorPendingGeneration(pendingGeneration));
    });
  }, [videoGenerations, pendingGenerations, dispatch]);

  // This is a failsafe. If we fetch a new batch of videos,
  // and one of them is completed and has the generationId of a pending generation, remove
  // the pending generation. I think there is a small window when this could be needed
  // if the video completes right when we are making the video_init call.
  useEffect(() => {
    const pendingIds = new Set(pendingGenerations.map(p => p.generationId));
    const completedVideos = videoGenerations.filter(
      video => video.status !== 'requested' && pendingIds.has(video.generationId)
    );

    completedVideos.forEach(video => {
      dispatch(removeVideoCreatorPendingGeneration(video.generationId));
    });
  }, [videoGenerations, pendingGenerations, dispatch]);

  const showPlaceholder = useMemo(() => {
    const hasCompletedVideos = videoGenerations.some(video => video.status !== 'requested');
    return pendingGenerations.length === 0 && !hasCompletedVideos;
  }, [pendingGenerations, videoGenerations]);

  const handleScrolledToBottom = useCallback(() => {
    if (loadMoreInFlightRef.current || isFetchingRef.current) {
      return;
    }
    const now = Date.now();
    if (now < loadMoreCooldownUntilRef.current) {
      return;
    }
    loadMoreInFlightRef.current = true;
    loadMoreCooldownUntilRef.current = now + LOAD_MORE_COOLDOWN_MS;
    fetchNext();
  }, [fetchNext]);

  // OnScrollBottom only listens on window; relay scroll from the real scroll container
  // (passed in from VideoCreator / VideoCreatorMobile).
  useEffect(() => {
    const scrollEl = scrollContainerRef.current;
    if (!scrollEl || typeof window === 'undefined') {
      return;
    }

    const relayScrollToWindow = () => {
      window.dispatchEvent(new Event('scroll'));
    };

    scrollEl.addEventListener('scroll', relayScrollToWindow, { passive: true });
    return () => {
      scrollEl.removeEventListener('scroll', relayScrollToWindow);
    };
  }, [scrollContainerRef]);

  return (
    <div className={cn(s['root'], isMobile && s['mobile'])}>
      <div className={s['header']}>
        <div className={s['header-title']}>{t('studio.videoCreator.resultsLabel')}</div>
        <div className={s['header-detail']}>
          <ModerationAlert className={s['alert-icon']} size={IconSize.SMALL} />
          <div className={s['alert-text']}>{t('studio.videoCreator.resultsDisclaimer')}</div>
        </div>
      </div>

      {showPlaceholder && <VideoCreatorPlaceholder />}
      {pendingGenerations.length > 0 && (
        <>
          {pendingGenerations.map(pendingGeneration => (
            <VideoPendingGeneration
              key={pendingGeneration.generationId}
              pendingGeneration={pendingGeneration}
            />
          ))}
        </>
      )}
      {videoGenerations.length > 0 &&
        videoGenerations.map(video =>
          video.status !== 'requested' ? (
            <VideoFile key={video.generationId} videoGeneration={video} />
          ) : null
        )}
      {hasMore ? (
        <OnScrollBottom
          onScrolledToBottom={handleScrolledToBottom}
          distanceToBottom={SCROLL_FETCH_DISTANCE_PX}
          distanceUnit={DISTANCE_UNIT.PX}
        >
          {/* OnScrollBottom behaves differently if it has a child. We need a throwaway element
              to trigger the right way of measuring distance to bottom. */}
          <div className={s['scroll-sentinel']} aria-hidden />
        </OnScrollBottom>
      ) : null}
    </div>
  );
};

VideoCreatorFiles.displayName = 'VideoCreatorFiles';
export default VideoCreatorFiles;
